/**
 * DeleteAccount — public webpage for users who don't have the app installed
 * anymore but still want to request data deletion. Google Play Data
 * Deletion policy requires BOTH an in-app flow (Profile → Delete account)
 * AND a public URL like this one.
 *
 * Behavior: user submits their email + optional reason. Backend logs the
 * request into `account_deletion_requests`, emails the DPO, and shows the
 * user a friendly confirmation that we'll process within 30 days.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Trash2, CheckCircle2, ChevronLeft, Loader2 } from 'lucide-react';
import api from '../lib/api';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

export default function DeleteAccount() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null); // response message

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/auth/delete-account-request', {
        email: email.trim().toLowerCase(),
        reason: reason.trim() || undefined,
      });
      setSubmitted(data?.message || 'We received your request.');
    } catch (err) {
      // Show a helpful message on either 4xx or network error — this page is
      // public so we don't want any raw error strings leaking to end users.
      setSubmitted(
        "We couldn't submit that just now. Please email sarvaritam@gmail.com and we'll delete your data within 30 days."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg relative pb-20">
      <div className="stars-bg" />
      <header className="relative border-b border-white/8 bg-black/30 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link
            to="/"
            data-testid="delete-account-back"
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
            aria-label="Back to home"
          >
            <ChevronLeft size={18} />
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-display font-bold text-lg hidden sm:inline">WorldVibe</span>
          </Link>
          <span className="ml-auto text-[10px] uppercase tracking-[0.22em] font-semibold text-red-300 flex items-center gap-1">
            <Trash2 size={12} /> Data deletion
          </span>
        </div>
      </header>

      <main className="relative max-w-2xl mx-auto px-5 pt-8 space-y-6" data-testid="delete-account-page">
        <section
          className="relative overflow-hidden rounded-3xl border border-white/12 p-6 sm:p-8"
          style={{
            background:
              'radial-gradient(circle at 15% 0%, rgba(244,63,94,0.25), transparent 55%),' +
              'radial-gradient(circle at 100% 100%, rgba(190,18,60,0.20), transparent 60%),' +
              '#0F0F1B',
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-red-300 font-semibold flex items-center gap-1">
            <Shield size={12} /> Request data deletion
          </div>
          <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight">
            Delete your WorldVibe account.
          </h1>
          <p className="mt-3 text-white/70 text-sm leading-relaxed">
            You can also delete your account directly inside the app —
            <b className="text-white"> Profile → Settings → Delete account</b>. If you no longer have
            the app installed, submit the form below and we&apos;ll process your request within{' '}
            <b className="text-white">30 days</b>.
          </p>
        </section>

        {/* What we delete */}
        <section className="rounded-2xl border border-white/10 bg-[#0F0F1A] p-5 sm:p-6 text-sm text-white/80 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-white/45 mb-2">What gets deleted</div>
          <ul className="list-disc pl-5 space-y-1 leading-relaxed">
            <li>Your profile, avatar, banner, bio, country, and settings</li>
            <li>Feed posts, comments, likes you created</li>
            <li>Quest streak history and daily quest responses</li>
            <li>Follows, followers, adopted pets, dating profile, notifications</li>
            <li>DMs and planet messages you sent are replaced with <i>&quot;message removed&quot;</i> so the other side&apos;s history stays coherent (per Google Play guidance)</li>
            <li>Your Stars, Credits, and XP are forfeited and cannot be restored</li>
          </ul>
          <div className="text-[11px] text-white/45 pt-2">
            Backups may retain minimal audit logs (deletion timestamp + user id) for up to 30 days for compliance.
          </div>
        </section>

        {submitted ? (
          <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-5 flex items-start gap-3" data-testid="delete-account-success">
            <CheckCircle2 size={20} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Request received</div>
              <p className="text-white/75 text-sm mt-1 leading-relaxed">{submitted}</p>
              <Link to="/" className="inline-block mt-3 text-emerald-300 hover:text-emerald-200 text-sm font-medium">
                ← Back to home
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0F0F1A] p-5 sm:p-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/50">Account email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-testid="delete-account-email"
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="you@example.com"
                autoComplete="email"
                disabled={busy}
              />
              <div className="text-[11px] text-white/45 mt-1">The email you used to sign up for WorldVibe.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/50">Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                maxLength={500}
                data-testid="delete-account-reason"
                className="bg-white/5 border-white/10 text-white min-h-[80px] mt-1"
                placeholder="Anything you'd like us to know before you go…"
                disabled={busy}
              />
            </div>
            <Button
              type="submit"
              disabled={!email.trim() || busy}
              data-testid="delete-account-submit"
              className="w-full bg-red-500 hover:bg-red-600 text-white border-0 font-semibold"
            >
              {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <Trash2 size={14} className="mr-1" />}
              Request deletion
            </Button>
          </form>
        )}

        <div className="text-center text-xs text-white/40 pt-2">
          Questions? Email{' '}
          <a href="mailto:sarvaritam@gmail.com" className="text-white/60 hover:text-white underline">sarvaritam@gmail.com</a>.
        </div>
      </main>
    </div>
  );
}
